// /client/src/components/Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Navigation from './Navigation';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('personal');
  
  // Personal info state
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    status: 'active',
    lastLogin: null,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Business info state
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    legalName: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    websiteUrl: '',
    bio: ''
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true
  });

  // Privacy preferences
  const [privacy, setPrivacy] = useState({
    profileVisible: true,
    showEmail: false,
    showPhone: false
  });

  // Theme preference
  const [theme, setTheme] = useState('auto');

  // US States list
  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  // Loading and message states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load user data on mount
  useEffect(() => {
    fetchUserData();
    fetchCompanyData();
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/contacts/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userData = data.contact;

        setPersonalInfo({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          bio: userData.bio || '',
          status: userData.status || 'active',
          lastLogin: userData.lastLogin || null,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        setNotifications({
          email: userData.preferences?.notifications?.email ?? true,
          sms: userData.preferences?.notifications?.sms ?? false,
          push: userData.preferences?.notifications?.push ?? true
        });

        setPrivacy({
          profileVisible: userData.preferences?.privacy?.profileVisible ?? true,
          showEmail: userData.preferences?.privacy?.showEmail ?? false,
          showPhone: userData.preferences?.privacy?.showPhone ?? false
        });

        setTheme(userData.preferences?.theme || 'auto');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/company', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const company = data.company;

        setBusinessInfo({
          name: company.name || '',
          legalName: company.legalName || '',
          phone: company.phone || '',
          email: company.email || '',
          street: company.address?.street || '',
          city: company.address?.city || '',
          state: company.address?.state || '',
          zipCode: company.address?.zipCode || '',
          country: company.address?.country || '',
          websiteUrl: company.websiteUrl || '',
          bio: company.bio || ''
        });
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      // Don't show error if company doesn't exist yet
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBusinessInfoChange = (field, value) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (field) => {
    setNotifications(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePrivacyChange = (field) => {
    setPrivacy(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSavePersonal = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (!personalInfo.firstName.trim() || !personalInfo.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!personalInfo.email.trim()) {
      setError('Email is required');
      return;
    }

    // Password change validation
    if (personalInfo.newPassword || personalInfo.confirmPassword) {
      if (!personalInfo.currentPassword) {
        setError('Current password is required to change password');
        return;
      }

      if (personalInfo.newPassword !== personalInfo.confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      if (personalInfo.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return;
      }
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const updateData = {
        email: personalInfo.email,
        profile: {
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          phone: personalInfo.phone,
          bio: personalInfo.bio
        },
        status: personalInfo.status,
        preferences: {
          notifications,
          privacy,
          theme
        }
      };

      // Update user info
      const response = await fetch(`/api/contacts/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }

      // If password change was requested, handle separately
      if (personalInfo.newPassword) {
        const passwordResponse = await fetch('/api/auth/change-password', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            currentPassword: personalInfo.currentPassword,
            newPassword: personalInfo.newPassword
          })
        });

        if (!passwordResponse.ok) {
          const data = await passwordResponse.json();
          throw new Error(data.error || 'Failed to change password');
        }

        // Clear password fields
        setPersonalInfo(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }

      setSuccessMessage('Personal information updated successfully');
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error saving personal info:', error);
      setError(error.message || 'Failed to update personal information');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Get token before clearing
      const token = localStorage.getItem('token');
      
      // Call backend logout endpoint if token exists
      if (token) {
        try {
          await axios.post('/api/auth/logout', {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (apiError) {
          // Continue with logout even if backend call fails
          console.error('Logout API call failed:', apiError);
        }
      }
    } finally {
      // Clear all auth data manually (don't rely on AuthContext yet)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.clear();
      
      // Clear all cookies
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
      
      // Clear axios default headers
      delete axios.defaults.headers.common['Authorization'];
      
      // Navigate FIRST with the parameter
      window.location.href = '/login?logout=success';
      
      // Then call logout to update context (this might redirect but window.location takes precedence)
      logout();
    }
  };

  const handleSaveBusiness = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (!businessInfo.name.trim() || !businessInfo.legalName.trim()) {
      setError('Company name and legal name are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const companyData = {
        name: businessInfo.name,
        legalName: businessInfo.legalName,
        phone: businessInfo.phone,
        email: businessInfo.email,
        address: {
          street: businessInfo.street,
          city: businessInfo.city,
          state: businessInfo.state,
          zipCode: businessInfo.zipCode,
          country: businessInfo.country
        },
        websiteUrl: businessInfo.websiteUrl,
        bio: businessInfo.bio
      };

      const response = await fetch('/api/company', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(companyData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update company information');
      }

      setSuccessMessage('Company information updated successfully');
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error saving company info:', error);
      setError(error.message || 'Failed to update company information');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container mt-6 pt-6">
          <div className="has-text-centered p-6">
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
                <h1 className='h1 my-2'>Profile Settings</h1>
                <button 
                    onClick={handleLogout}
                    type="button"
                    className="mx-5 menu button"
                >
                    Logout
                </button>
            </div>

            {successMessage && (
              <div className="notification is-success">
                <button 
                  className="delete" 
                  onClick={() => setSuccessMessage('')}
                ></button>
                {successMessage}
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
              </div>
            )}

            {/* Tabs */}
            <div className="my-0 py-0 tabs is-boxed">
              <ul className='my-0 py-0 '>
                <li className={activeTab === 'personal' ? 'mb-0 pb-0 is-bold is-active' : 'mb-0 pb-0'}>
                  <a onClick={() => setActiveTab('personal')}>
                    Personal Information
                  </a>
                </li>
                <li className={activeTab === 'business' ? 'mb-0 pb-0 is-bold is-active' : 'mb-0 pb-0'}>
                  <a onClick={() => setActiveTab('business')}>
                    Company Information
                  </a>
                </li>
              </ul>
            </div>

            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div className="mt-0 mb-6 box">
                <form onSubmit={handleSavePersonal}>
                  <h2 className="title is-4 mb-2">Personal Information</h2>

                  <div className="my-0 py-0 columns">
                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">First Name *</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={personalInfo.firstName}
                            onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Last Name *</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={personalInfo.lastName}
                            onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-0 py-0 columns">
                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Email *</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="email"
                            value={personalInfo.email}
                            onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Phone</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="tel"
                            value={personalInfo.phone}
                            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-0 py-0 field">
                    <label className="label">Bio</label>
                    <div className="my-0 py-0 control">
                      <textarea
                        className="textarea"
                        value={personalInfo.bio}
                        onChange={(e) => handlePersonalInfoChange('bio', e.target.value)}
                        disabled={saving}
                        rows={4}
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                  </div>

                  <hr />

                  <h3 className="title is-5 mb-2">Change Password</h3>
                  <p className="mb-4 small">Leave blank if you don't want to change your password.</p>

                  <div className="my-0 py-0 field">
                    <label className="label">Current Password</label>
                    <div className="my-0 py-0 control">
                      <input
                        className="input"
                        type="password"
                        value={personalInfo.currentPassword}
                        onChange={(e) => handlePersonalInfoChange('currentPassword', e.target.value)}
                        disabled={saving}
                        autoComplete="current-password"
                      />
                    </div>
                  </div>

                  <div className="columns">
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">New Password</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="password"
                            value={personalInfo.newPassword}
                            onChange={(e) => handlePersonalInfoChange('newPassword', e.target.value)}
                            disabled={saving}
                            autoComplete="new-password"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Confirm New Password</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="password"
                            value={personalInfo.confirmPassword}
                            onChange={(e) => handlePersonalInfoChange('confirmPassword', e.target.value)}
                            disabled={saving}
                            autoComplete="new-password"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <h3 className="title is-5 mb-4">Notification Preferences</h3>

                  <div className="ml-2 field">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={notifications.email}
                        onChange={() => handleNotificationChange('email')}
                        disabled={saving}
                      />
                      <span className="ml-2">Email notifications</span>
                    </label>
                  </div>

                  <div className="ml-2 field">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={notifications.sms}
                        onChange={() => handleNotificationChange('sms')}
                        disabled={saving}
                      />
                      <span className="ml-2">SMS notifications</span>
                    </label>
                  </div>

                  {personalInfo.lastLogin && (
                    <div className="my-0 py-0 field">
                        <label className="label">Last Login</label>
                        <div className="control">
                        <input
                            className="input"
                            type="text"
                            value={new Date(personalInfo.lastLogin).toLocaleString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                            })}
                            disabled
                            readOnly
                        />
                        </div>
                    </div>
                    )}

                  <div className="field is-grouped pt-2 mt-5">
                    <div className="control">
                      <button 
                        type="submit"
                        className={`button is-primary ${saving ? 'is-loading' : ''}`}
                        disabled={saving}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* Business Information Tab */}
            {activeTab === 'business' && (
              <div className="mb-6 box">
                <form onSubmit={handleSaveBusiness}>
                  <h2 className="title is-4 mb-4">Company Information</h2>
                  <p className="mb-4">
                    This information is shared across all administrators and represents your organization.
                  </p>

                  <div className="my-0 py-0 columns">
                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Company Name *</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={businessInfo.name}
                            onChange={(e) => handleBusinessInfoChange('name', e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Legal Name *</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={businessInfo.legalName}
                            onChange={(e) => handleBusinessInfoChange('legalName', e.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-0 py-0 columns">
                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Phone Number</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="tel"
                            value={businessInfo.phone}
                            onChange={(e) => handleBusinessInfoChange('phone', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="my-0 py-0 column is-6">
                      <div className="my-0 py-0 field">
                        <label className="label">Email Address</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="email"
                            value={businessInfo.email}
                            onChange={(e) => handleBusinessInfoChange('email', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-0 py-0 field">
                    <label className="label">Website URL</label>
                    <div className="my-0 py-0 control">
                      <input
                        className="input"
                        type="url"
                        value={businessInfo.websiteUrl}
                        onChange={(e) => handleBusinessInfoChange('websiteUrl', e.target.value)}
                        disabled={saving}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  <hr />

                  <h3 className="title is-5 mb-4">Company Address</h3>

                  <div className="my-0 py-0 field">
                    <label className="label">Street Address</label>
                    <div className="my-0 py-0 control">
                      <input
                        className="input"
                        type="text"
                        value={businessInfo.street}
                        onChange={(e) => handleBusinessInfoChange('street', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>

                  <div className="columns">
                    <div className="column is-4">
                      <div className="my-0 py-0 field">
                        <label className="label">City</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={businessInfo.city}
                            onChange={(e) => handleBusinessInfoChange('city', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="my-0 py-0 field">
                        <label className="label">State</label>
                        <div className="my-0 py-0 control">
                          <div className="select is-fullwidth">
                            <select
                              value={businessInfo.state}
                              onChange={(e) => handleBusinessInfoChange('state', e.target.value)}
                              disabled={saving}
                            >
                              <option value="">Select a state...</option>
                              {US_STATES.map(state => (
                                <option key={state} value={state}>
                                  {state}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="my-0 py-0 field">
                        <label className="label">ZIP Code</label>
                        <div className="my-0 py-0 control">
                          <input
                            className="input"
                            type="text"
                            value={businessInfo.zipCode}
                            onChange={(e) => handleBusinessInfoChange('zipCode', e.target.value)}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="my-0 py-0 field">
                    <label className="label">Company Bio</label>
                    <div className="my-0 py-0 control">
                      <textarea
                        className="textarea"
                        value={businessInfo.bio}
                        onChange={(e) => handleBusinessInfoChange('bio', e.target.value)}
                        disabled={saving}
                        rows={6}
                        placeholder="A brief description of your company..."
                      />
                    </div>
                  </div>

                  <div className="field is-grouped pt-2 mt-5">
                    <div className="control">
                      <button 
                        type="submit"
                        className={`button is-primary ${saving ? 'is-loading' : ''}`}
                        disabled={saving}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

          </div>

        </div>
      </section>
    </>
  );
};

export default Profile;